import React, { useState, useEffect } from "react";
import { Button, Spinner, Row, Col, Alert, Card, CardBody } from "reactstrap";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import mantisLogo from "../Assets/img/icons/b2b/mantisBlack.png";
import {
  getMantisBusiness,
  getMantisBusinessDocumentQueries,
  updateMantisBusiness,
} from "../ApiCalls/backoffice";

const select = (state) => {
  return {
    userId: state.auth.userId,
    token: state.auth.token,
    user: state.auth.user,
    mantisBusiness: state.auth.mantisBusiness,
  };
};

function MantisBusinessDetail(props) {
  const [mantisBusiness, setMantisBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approvingBusiness, setApprovingBusiness] = useState(false);
  const [businessQueries, setBusinessQueries] = useState([]);
  const [alertMessage, setAlertMessage] = useState("");

  const { id } = useParams();

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; getData is redefined each render and would cause a refetch loop
  }, []);

  useEffect(() => {
    if (mantisBusiness) {
      getBusinessQueries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getBusinessQueries is redefined each render; adding it would re-run this effect on every render
  }, [mantisBusiness]);

  async function getData() {
    await getBusiness();
    setLoading(false);
  }

  async function getBusiness() {
    const businessRes = await getMantisBusiness(props.token, props.userId, id);

    if (businessRes.success) {
      setMantisBusiness(businessRes.mantisBusiness);
    }
  }

  async function getBusinessQueries() {
    const businessQueriesRes = await getMantisBusinessDocumentQueries(
      props.userId,
      mantisBusiness._id,
      props.token
    );
    if (businessQueriesRes.success) {
      setBusinessQueries(businessQueriesRes.queries);
    }
  }

  async function handleVerifyBusiness() {
    if (!approvingBusiness) {
      setApprovingBusiness(true);
      const res = await updateMantisBusiness(
        props.userId,
        id,
        { approved: !mantisBusiness.approved },
        props.token
      );
      if (res.success) {
        setMantisBusiness(res.updatedBusiness);
        setAlertMessage("Negocio actualizado correctamente");
      } else {
        setAlertMessage("Error al verificar el negocio");
      }
      setApprovingBusiness(false);
    }
  }

  if (loading) {
    return (
      <div className="mantis-businesses-page">
        <div className="loading-container">
          <Spinner color="primary" />
          <p>Cargando detalles del negocio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mantis-businesses-page">
      {/* The banner lives in the tabs wrapper so every tab shows it */}
      <div className="page-content">
        {/* Alert Messages */}
        {alertMessage && (
          <Row>
            <Col xs={12} md={8} style={{ margin: "0 auto 2rem auto" }}>
              <Alert
                color="info"
                isOpen={alertMessage !== ""}
                toggle={() => setAlertMessage("")}
                className="custom-alert"
              >
                {alertMessage}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Business Header Card */}
        <Row>
          <Col xs={12}>
            <Card className="business-header-card">
              <CardBody>
                <div className="business-header-content">
                  <div className="business-logo-section">
                    <div className="business-logo-container">
                      <img
                        src={mantisBusiness?.logo || mantisLogo}
                        alt="Logo del negocio"
                        className="business-logo"
                      />
                    </div>
                    <div className="business-info">
                      <h2 className="business-name">
                        {mantisBusiness?.name || ""}
                      </h2>
                      {mantisBusiness?.approved && (
                        <span className="verification-badge">
                          <i className="fas fa-check-circle"></i>
                          Verificada
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="verification-actions">
                    <Button
                      onClick={handleVerifyBusiness}
                      disabled={approvingBusiness}
                      className="verification-button"
                    >
                      {approvingBusiness ? (
                        <span className="button-content">
                          <Spinner size="sm" />
                          Procesando...
                        </span>
                      ) : mantisBusiness?.approved ? (
                        <span className="button-content">
                          <i className="fas fa-times"></i>
                          Quitar verificación
                        </span>
                      ) : (
                        <span className="button-content">
                          <i className="fas fa-check"></i>
                          Verificar
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Contact Information Card */}
        <Row>
          <Col xs={12} md={6}>
            <Card className="info-card">
              <CardBody>
                <h3 className="card-title">
                  <i className="fas fa-address-book"></i>
                  Información de contacto
                </h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Nombre:</span>
                    <span className="info-value">
                      {mantisBusiness?.contactFirstName || ""}{" "}
                      {mantisBusiness?.contactLastName || ""}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">
                      {mantisBusiness?.contactEmail || ""}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Teléfono:</span>
                    <span className="info-value">
                      {mantisBusiness?.contactPhone || ""}
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xs={12} md={6}>
            <Card className="info-card">
              <CardBody>
                <h3 className="card-title">
                  <i className="fas fa-building"></i>
                  Información de la empresa
                </h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Tipo de identificación:</span>
                    <span className="info-value">
                      {mantisBusiness?.identificationType || ""}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">
                      Número de identificación:
                    </span>
                    <span className="info-value">
                      {mantisBusiness?.identificationNumber || ""}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Dirección:</span>
                    <span className="info-value">
                      {mantisBusiness?.address || ""}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Países:</span>
                    <span className="info-value">
                      {mantisBusiness?.countries?.join(", ") || ""}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Ciudades:</span>
                    <span className="info-value">
                      {mantisBusiness?.cities?.join(", ") || ""}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Sitio web:</span>
                    <span className="info-value">
                      {mantisBusiness?.website || ""}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Código de referido:</span>
                    <span className="info-value">
                      {mantisBusiness?.referralCode || ""}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Fecha de creación:</span>
                    <span className="info-value">
                      {mantisBusiness?.dateCreated
                        ? new Date(
                            mantisBusiness.dateCreated
                          ).toLocaleDateString("es")
                        : ""}
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Statistics Card */}
        <Row>
          <Col xs={12}>
            <Card className="info-card">
              <CardBody>
                <h3 className="card-title">
                  <i className="fas fa-chart-line"></i>
                  Estadísticas
                </h3>
                <div className="stats-content">
                  <div className="stat-item">
                    <div className="stat-icon">
                      <i className="fas fa-search"></i>
                    </div>
                    <div className="stat-info">
                      <span className="stat-value">
                        {businessQueries.length}
                      </span>
                      <span className="stat-label">Consultas realizadas</span>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default connect(select)(MantisBusinessDetail);
